import type { BusAssetBaseImagesLoose, BusAssetCoverImagesLoose } from '@lib/shared';
import { BUS_ASSET_BASE_IMAGES, BUS_ASSET_COVER_IMAGES } from '@lib/shared';

import type {
    AuctionItemEntity,
    AuctionListingItemEntity,
    DirectSaleItemEntity,
    DirectSaleListingItemEntity,
    SaleAssetBusEntity,
    SaleAssetBusListingItem,
} from '~/apiClient';

type UseA11yReturn = {
    auctionDetail: (auction: AuctionItemEntity | AuctionListingItemEntity) => string;
    directSaleDetail: (directSale: DirectSaleItemEntity | DirectSaleListingItemEntity) => string;
    saleImageAlt: (asset: SaleAssetBusEntity | SaleAssetBusListingItem, imageSource?: string) => string | undefined;
    saleVideoAlt: (asset: SaleAssetBusEntity | SaleAssetBusListingItem, videoSource?: string) => string | undefined;
};

export const useA11y = (): UseA11yReturn => {
    const { tt } = useTypedI18n();

    return {
        auctionDetail: (auction: AuctionItemEntity | AuctionListingItemEntity): TypedTranslateReturn =>
            tt('a11y.auctionDetail', { name: `${auction.mainAsset.brand.brandValue}, ${auction.mainAsset.model.modelValue}` }),
        directSaleDetail: (directSale: DirectSaleItemEntity | DirectSaleListingItemEntity): TypedTranslateReturn =>
            tt('a11y.directSaleDetail', { name: `${directSale.mainAsset.brand.brandValue}, ${directSale.mainAsset.model.modelValue}` }),

        saleImageAlt: (asset: SaleAssetBusEntity | SaleAssetBusListingItem, imageSource?: string): string | undefined => {
            if (!imageSource) return;

            const descriptiveImageName = imageSource.split('/').pop() || '';

            const coverImage = (BUS_ASSET_COVER_IMAGES as BusAssetCoverImagesLoose)?.[descriptiveImageName];
            const baseImage = (BUS_ASSET_BASE_IMAGES as BusAssetBaseImagesLoose)?.[descriptiveImageName];
            const label = coverImage?.label || baseImage?.label || 'Image';

            return `${asset.brand.brandValue} ${asset.model.modelValue} - ${label}`;
        },
        saleVideoAlt: (asset: SaleAssetBusEntity | SaleAssetBusListingItem, videoSource?: string): string | undefined => {
            if (!videoSource) return;

            const name = `${asset.brand.brandValue} ${asset.model.modelValue}`;
            const labelKey = saleVideoLabelKey(videoSource);

            return labelKey ? `${name} - ${tt(labelKey)}` : name;
        },
    };
};
